import {
    useEffect,
    useMemo, useState,
} from 'react'

import {
    DetailPanelComponentProps,
} from 'app/shared-components/SkyNetDataGridExpandable'
import FormWithControls from 'app/shared-components/FormWithControls'
import useEnumValues from 'app/hooks/useEnumValues'
import longTermLeaseInvoicesRequests from 'app/requests/LongTermLease/LongTermLeaseInvoices.request'
import useGetApplicableCurrency from 'app/requests/ContractBasis/hooks/useGetApplicableCurrency'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    InvoiceDesignation,
} from 'app/enums/label.enums'
import WarningCard from 'app/shared-components/WarningCard'
import {
    LongTermLeaseInvoiceApplicableCurrency,
} from 'app/Apps/Pricing/ContractBasis/LongTermLeaseFees/LongTermLeaseFees.types'

import useLongTermLeaseInvoicesContext from '../hooks/useLongTermLeaseInvoicesContext'
import getFields from './longTermLeaseInvoicesCreate.fields'

const convertToSelectorOptions = (applicableCurrency: LongTermLeaseInvoiceApplicableCurrency[]) => {
    return applicableCurrency?.reduce((acc, element) => {
        return {
            ...acc,
            [element.id]: element.currency,
        }
    }, {})
}

const LongTermLeaseInvoicesCreate = ({
    onSuccess,
    onCancel,
}: Readonly<DetailPanelComponentProps>) => {
    const [
        value,
        setValue,
    ] = useState<{
        invoiceDesignation: InvoiceDesignation,
        longTermLeaseFees: {
            id: number,
        }
    }>()

    const getAllowedValues = useEnumValues()

    const {
        longTermLease,
    } = useLongTermLeaseInvoicesContext()

    const {
        data: applicableCurrency,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetApplicableCurrency({
        pricingAccountId: longTermLease?.pricingAccount.id,
        customerQuotationId: longTermLease?.customerExecution.id,
    })

    useEffect(() => {
        if (applicableCurrency?.length === 1 && !value?.longTermLeaseFees) {
            setValue({
                ...value,
                longTermLeaseFees: {
                    id: applicableCurrency[0].id,
                },
            })
        }
    }, [
        applicableCurrency,
        value,
    ])

    const requestParams = useMemo(() => {
        return longTermLeaseInvoicesRequests.createLongTermLeaseInvoice({
            longTermLeaseId: longTermLease?.id,
        })
    }, [longTermLease?.id])

    const fields = useMemo(() => {
        return getFields({
            getAllowedValues,
            pricingCurrencyAllowedValues: convertToSelectorOptions(applicableCurrency),
            pricingAccount: longTermLease?.pricingAccount,
            customerExecution: longTermLease?.customerExecution,
        })
    }, [
        applicableCurrency,
        getAllowedValues,
        longTermLease,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                {!applicableCurrency?.length && (
                    <WarningCard>
                        Valid Long-Term Lease Fees cannot be found for invoice creation.
                        Please contact the Pricing Team.
                    </WarningCard>
                )}
                <FormWithControls
                    name="LongTermLeaseInvoicesCreate"
                    value={value}
                    setExternalValue={setValue}
                    fields={fields}
                    requestParams={requestParams}
                    onSuccess={onSuccess}
                    onCancel={onCancel}
                    createEnabled={Boolean(value?.invoiceDesignation && value?.longTermLeaseFees)}
                    cancelEnabled
                />
            </>
        </StatusHandler>
    )
}

export default LongTermLeaseInvoicesCreate
